import DOMPurify from 'dompurify';

export function $parseErrorMessage(
  error: unknown,
  defaultMessage: string = 'An error occurred.',
): string {
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const rawMessage = (error as { message?: unknown }).message;
    const message = String(rawMessage ?? defaultMessage);

    if (/<no response>|Failed to fetch/i.test(message)) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }

    return message;
  } else if (typeof error === 'string') {
    if (/<no response>|Failed to fetch/i.test(error)) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }

    return error;
  }

  return defaultMessage;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function $getApiError(error: any): {
  message: string;
  errors: Record<string, string[]> | null;
  error_code: string | null;
} {
  if (typeof error === 'object' && error !== null) {
    return {
      message: $parseErrorMessage(
        error,
        error.data?.message ?? 'An error occurred.',
      ),
      errors: error.data?.errors,
      error_code: error.data?.error_code,
    };
  }

  return {
    message: $parseErrorMessage(error),
    errors: null,
    error_code: null,
  };
}

export function $getApiErrorStatus(error: unknown): number | undefined {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    return (error as { status?: number }).status;
  }
  return undefined;
}

export function $sanitizeHtml(html: string | undefined): string {
  if (!html) return '';

  if (import.meta.server) {
    return html;
  }

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'p',
      'br',
      'hr',
      'ul',
      'ol',
      'li',
      'a',
      'strong',
      'em',
      'b',
      'i',
      'u',
      's',
      'blockquote',
      'pre',
      'code',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'div',
      'span',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    ALLOW_DATA_ATTR: false,
  });
}

export function intervalToLabel(interval: string, intervalCount = 1) {
  if (!interval) return '';

  // Normalize
  interval = interval.toLowerCase();

  // Common Stripe cases
  if (interval === 'month') {
    if (intervalCount === 1) return 'Monthly';
    if (intervalCount === 3) return 'Quarterly';
    if (intervalCount === 6) return 'Semi-Annual';
    if (intervalCount === 12) return 'Yearly';
    return `Every ${intervalCount} Months`;
  }

  if (interval === 'year') {
    if (intervalCount === 1) return 'Yearly';
    return `Every ${intervalCount} Years`;
  }

  if (interval === 'week') {
    if (intervalCount === 1) return 'Weekly';
    return `Every ${intervalCount} Weeks`;
  }

  if (interval === 'day') {
    if (intervalCount === 1) return 'Daily';
    return `Every ${intervalCount} Days`;
  }

  return '';
}

export function intervalToPriceUnit(interval: string, intervalCount = 1) {
  if (!interval) return '';

  interval = interval.toLowerCase();

  if (intervalCount === 1) {
    return `${interval}`;
  }

  return `${intervalCount} ${interval}s`;
}

export function membershipTierToLabel(tier: string) {
  if (!tier) return '';

  const map = {
    ANNUAL: 'Yearly',
    SEMI_ANNUAL: 'Semi-Annual',
    MONTHLY: 'Monthly',
    QUARTERLY: 'Quarterly',
    WEEKLY: 'Weekly',
    LIFETIME: 'Lifetime',
  };

  return map[tier as keyof typeof map] ?? tier;
}

export function $getFileNameWithoutExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.substring(0, lastDot) : fileName;
}
